import { useEffect, useState, type FormEvent } from 'react';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Label,
  Progress,
} from '@ludolio/shared-ui';
import { CheckCircle2, ChevronDown, ChevronUp, Loader2, Plus, Users } from 'lucide-react';
import { useUnitsModule } from '../../module-context';
import type { AddStudentsResult, InvitationBatchProgress, UnitStudent } from '../../types';
import { UnitStudentsTable } from '../../UnitStudentsTable';

interface StudentsTabProps {
  unitId: string;
  students: UnitStudent[];
  onDataChanged(): Promise<void>;
}

const STUDENT_IMPORT_BATCH_SIZE = 200;

const createEmptyResult = (): AddStudentsResult => ({
  added: [],
  pending: [],
  alreadyEnrolled: [],
  alreadyPending: [],
  invalidDomain: [],
  invalidFormat: [],
  invitationEmailFailed: [],
  invitationBatchId: null,
  invitationJobsQueued: 0,
});

function mergeResults(left: AddStudentsResult, right: AddStudentsResult): AddStudentsResult {
  return {
    added: [...left.added, ...right.added],
    pending: [...left.pending, ...right.pending],
    alreadyEnrolled: [...left.alreadyEnrolled, ...right.alreadyEnrolled],
    alreadyPending: [...left.alreadyPending, ...right.alreadyPending],
    invalidDomain: [...left.invalidDomain, ...right.invalidDomain],
    invalidFormat: [...left.invalidFormat, ...right.invalidFormat],
    invitationEmailFailed: [...left.invitationEmailFailed, ...right.invitationEmailFailed],
    invitationBatchId: right.invitationBatchId || left.invitationBatchId,
    invitationJobsQueued: left.invitationJobsQueued + right.invitationJobsQueued,
  };
}

function parseEmails(input: string): string[] {
  const seen = new Set<string>();
  return input
    .split(/[\n,]/)
    .map((email) => email.trim().toLowerCase())
    .filter((email) => {
      if (!email || seen.has(email)) return false;
      seen.add(email);
      return true;
    });
}

function CollapsibleEmailList({
  emails,
  previewCount = 3,
}: {
  emails: string[];
  previewCount?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  if (emails.length <= previewCount) return <span>{emails.join(', ')}</span>;

  return (
    <span>
      {(expanded ? emails : emails.slice(0, previewCount)).join(', ')}
      <button
        type="button"
        className="ml-1 inline-flex items-center gap-0.5 text-sm text-[hsl(var(--color-primary-text))] underline transition-colors hover:text-[hsl(var(--color-primary-text-hover))]"
        onClick={() => setExpanded((current) => !current)}
      >
        {expanded ? (
          <>show less <ChevronUp className="h-3 w-3" /></>
        ) : (
          <>and {emails.length - previewCount} more <ChevronDown className="h-3 w-3" /></>
        )}
      </button>
    </span>
  );
}

export function StudentsTab({ unitId, students, onDataChanged }: StudentsTabProps) {
  const { api, capabilities, reportError } = useUnitsModule();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [emailsInput, setEmailsInput] = useState('');
  const [result, setResult] = useState<AddStudentsResult | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [progress, setProgress] = useState<{ processed: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [complete, setComplete] = useState(false);
  const [activeBatchIds, setActiveBatchIds] = useState<string[]>([]);
  const [deliveryProgress, setDeliveryProgress] = useState<InvitationBatchProgress | null>(null);
  const [studentToRemove, setStudentToRemove] = useState<UnitStudent | null>(null);
  const [resendingInvitationId, setResendingInvitationId] = useState<string | null>(null);
  const [resendMessage, setResendMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const emailCount = parseEmails(emailsInput).length;

  const resetDialog = () => {
    setDialogOpen(false);
    setEmailsInput('');
    setResult(null);
    setError(null);
    setProgress(null);
    setActiveBatchIds([]);
    setDeliveryProgress(null);
    setComplete(false);
  };

  useEffect(() => {
    if (!activeBatchIds.length) return;
    let cancelled = false;
    const load = async () => {
      try {
        const responses = await Promise.all(
          activeBatchIds.map((batchId) => api.getInvitationBatchProgress(batchId))
        );
        if (cancelled) return;
        const batches = responses
          .filter((response) => response.success && response.data)
          .map((response) => response.data!);
        if (!batches.length) return;

        const counts: InvitationBatchProgress['counts'] = {};
        let totalJobs = 0;
        for (const batch of batches) {
          totalJobs += batch.totalJobs;
          for (const [status, count] of Object.entries(batch.counts)) {
            counts[status as keyof typeof counts] = (counts[status as keyof typeof counts] || 0) + (count || 0);
          }
        }
        const completedAt = batches.every((batch) => batch.completedAt)
          ? batches.map((batch) => batch.completedAt!).sort().at(-1) || null
          : null;
        setDeliveryProgress({
          id: batches[0].id,
          totalJobs,
          completedAt,
          counts,
        });
        if (completedAt) {
          setActiveBatchIds([]);
          await onDataChanged();
        }
      } catch (caughtError) {
        reportError(caughtError);
      }
    };
    void load();
    const timer = window.setInterval(() => void load(), 2000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [activeBatchIds, api, reportError]);

  const handleAddStudents = async (event: FormEvent) => {
    event.preventDefault();
    if (!capabilities.canManageStudents) return;

    const emails = parseEmails(emailsInput);
    if (!emails.length) {
      setResult(null);
      setError('Please enter at least one student email.');
      return;
    }

    setIsAdding(true);
    setComplete(false);
    setError(null);
    setResult(createEmptyResult());
    setProgress({ processed: 0, total: emails.length });
    setActiveBatchIds([]);
    setDeliveryProgress(null);
    let combinedResult = createEmptyResult();
    let processedCount = 0;
    const batchIds: string[] = [];

    try {
      for (let index = 0; index < emails.length; index += STUDENT_IMPORT_BATCH_SIZE) {
        const batch = emails.slice(index, index + STUDENT_IMPORT_BATCH_SIZE);
        const response = await api.addStudentsByEmail(unitId, batch);
        if (!response.success || !response.data) {
          throw new Error(response.error || 'Failed to add students');
        }
        combinedResult = mergeResults(combinedResult, response.data);
        processedCount += batch.length;
        setResult(combinedResult);
        setProgress({ processed: processedCount, total: emails.length });
        if (response.data.invitationBatchId) batchIds.push(response.data.invitationBatchId);
      }
      setComplete(true);
      if (batchIds.length) setActiveBatchIds(batchIds);
      if (combinedResult.added.length || combinedResult.pending.length) {
        await onDataChanged();
      }
    } catch (caughtError) {
      console.error('Failed to add students:', caughtError);
      reportError(caughtError);
      setError(
        processedCount
          ? `Import stopped after ${processedCount} of ${emails.length} email(s). Completed batches were saved, so you can retry the remaining emails.`
          : 'Failed to add students. Please try again.',
      );
      if (batchIds.length) setActiveBatchIds(batchIds);
      if (combinedResult.added.length || combinedResult.pending.length) {
        await onDataChanged();
      }
    } finally {
      setIsAdding(false);
      setComplete(true);
    }
  };

  const handleRemoveStudent = async () => {
    if (!studentToRemove || !capabilities.canManageStudents) return;
    try {
      if (studentToRemove.status === 'pending') {
        await api.removePendingEnrollment(unitId, studentToRemove.id);
      } else {
        await api.removeStudentFromUnit(unitId, studentToRemove.id);
      }
      setStudentToRemove(null);
      await onDataChanged();
    } catch (caughtError) {
      console.error('Failed to remove student:', caughtError);
      reportError(caughtError);
    }
  };

  const handleResendInvitation = async (student: UnitStudent) => {
    setResendingInvitationId(student.id);
    setResendMessage(null);
    try {
      const response = await api.resendStudentInvitation(unitId, student.id);
      if (!response.success || !response.data) throw new Error(response.error || 'Failed to queue invitation');
      setResendMessage({
        tone: 'success',
        text: response.data.created ? `Invitation to ${student.email} queued.` : `Invitation to ${student.email} is already being sent.`,
      });
      await onDataChanged();
    } catch (caughtError) {
      reportError(caughtError);
      setResendMessage({ tone: 'error', text: `Could not queue invitation to ${student.email}.` });
    } finally {
      setResendingInvitationId(null);
    }
  };

  return (
    <>
      <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-lg font-semibold">Enrolled Students</h3>
        {capabilities.canManageStudents && (
          <Dialog
            open={dialogOpen}
            onOpenChange={(open) => {
              if (!open && !isAdding) resetDialog();
              else setDialogOpen(open);
            }}
          >
            <DialogTrigger asChild>
              <Button><Plus className="mr-2 h-4 w-4" />Add Students</Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] w-[95vw] max-w-2xl overflow-x-hidden overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add Students by Email</DialogTitle>
                <DialogDescription>
                  Enter student email addresses (one per line or comma-separated)
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddStudents} className="space-y-4">
                {!isAdding && !complete && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="emails">Student Emails</Label>
                      {emailCount > 0 && (
                        <span className="text-sm text-muted-foreground">
                          {emailCount.toLocaleString()} email{emailCount !== 1 ? 's' : ''} detected
                        </span>
                      )}
                    </div>
                    <textarea
                      id="emails"
                      className="h-48 max-h-[40vh] w-full resize-none overflow-y-auto rounded-md border px-3 py-2"
                      placeholder={'student1@example.com\nstudent2@example.com\nstudent3@example.com'}
                      value={emailsInput}
                      onChange={(event) => setEmailsInput(event.target.value)}
                      required
                    />
                    {emailCount > STUDENT_IMPORT_BATCH_SIZE && (
                      <p className="text-sm text-muted-foreground">
                        Large imports are processed in batches of {STUDENT_IMPORT_BATCH_SIZE}. Invitation delivery continues in the background.
                      </p>
                    )}
                  </div>
                )}
                {isAdding && progress && (
                  <div className="space-y-3 py-2">
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      <span className="text-sm font-medium">Importing students…</span>
                    </div>
                    <Progress value={progress.processed} max={progress.total} className="h-3" />
                    <p className="text-center text-sm text-muted-foreground">
                      {progress.processed.toLocaleString()} of {progress.total.toLocaleString()} processed
                      {progress.total > STUDENT_IMPORT_BATCH_SIZE && (
                        <> · Batch {Math.min(Math.ceil(progress.processed / STUDENT_IMPORT_BATCH_SIZE) + 1, Math.ceil(progress.total / STUDENT_IMPORT_BATCH_SIZE))} of {Math.ceil(progress.total / STUDENT_IMPORT_BATCH_SIZE)}</>
                      )}
                    </p>
                  </div>
                )}
                {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
                {complete && result && (
                  <div className="space-y-3">
                    {!error && (
                      <div className="flex items-center gap-2 text-green-600">
                        <CheckCircle2 className="h-5 w-5" />
                        <span className="font-medium">Students added</span>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {[
                        { label: 'invalid format', count: result.invalidFormat.length, className: 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-400' },
                        { label: 'not eligible', count: result.invalidDomain.length, className: 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-400' },
                        { label: 'added', count: result.added.length, className: 'bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400' },
                        { label: 'pending', count: result.pending.length, className: 'bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400' },
                        { label: 'already enrolled', count: result.alreadyEnrolled.length, className: 'bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400' },
                        { label: 'already pending', count: result.alreadyPending.length, className: 'bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400' },
                        { label: 'email not sent', count: result.invitationEmailFailed.length, className: 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-400' },
                      ].map(({ label, count, className }) => (
                        count > 0 && (
                          <div key={label} className={`flex items-center gap-2 rounded-md p-2 ${className}`}>
                            <span className="font-semibold">{count.toLocaleString()}</span> {label}
                          </div>
                        )
                      ))}
                    </div>
                    {result.invalidFormat.length > 0 && (
                      <div className="text-sm text-muted-foreground">
                        <p className="mb-1 font-medium text-destructive">Invalid email format (missing @ or malformed):</p>
                        <CollapsibleEmailList emails={result.invalidFormat} previewCount={5} />
                      </div>
                    )}
                    {result.invalidDomain.length > 0 && (
                      <div className="text-sm text-muted-foreground">
                        <p className="mb-1 font-medium text-destructive">Not eligible for this organization:</p>
                        <CollapsibleEmailList emails={result.invalidDomain} previewCount={5} />
                      </div>
                    )}
                    {result.pending.length > 0 && (
                      <div className="text-sm text-muted-foreground">
                        <p className="mb-1 font-medium text-foreground">Pending students queued for invitation email:</p>
                        <CollapsibleEmailList emails={result.pending} previewCount={5} />
                      </div>
                    )}
                    {deliveryProgress && (
                      <div className="rounded-md bg-muted p-3 text-sm">
                        <p className="font-medium">Invitation delivery</p>
                        <p className="mt-1 text-muted-foreground">
                          {(deliveryProgress.counts.sent || 0).toLocaleString()} sent
                          {' · '}{((deliveryProgress.counts.queued || 0) + (deliveryProgress.counts.retry || 0) + (deliveryProgress.counts.sending || 0) + (deliveryProgress.counts.accepted || 0)).toLocaleString()} in progress
                          {' · '}{((deliveryProgress.counts.failed || 0) + (deliveryProgress.counts.uncertain || 0)).toLocaleString()} need attention
                        </p>
                      </div>
                    )}
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  {complete ? (
                    <Button type="button" onClick={resetDialog}>Done</Button>
                  ) : (
                    <>
                      <Button type="button" variant="outline" disabled={isAdding} onClick={resetDialog}>Cancel</Button>
                      <Button type="submit" disabled={isAdding || emailCount === 0}>
                        {isAdding && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {isAdding ? 'Importing…' : `Add ${emailCount > 0 ? `${emailCount.toLocaleString()} ` : ''}Student${emailCount !== 1 ? 's' : ''}`}
                      </Button>
                    </>
                  )}
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {students.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="mb-4 h-16 w-16 text-muted-foreground" />
            <h3 className="mb-2 text-lg font-semibold">No students enrolled</h3>
            <p className="mb-4 text-center text-muted-foreground">Add students to this unit to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="space-y-3 p-4">
            {resendMessage && (
              <Alert variant={resendMessage.tone === 'error' ? 'destructive' : 'default'}>
                <AlertDescription>{resendMessage.text}</AlertDescription>
              </Alert>
            )}
            <UnitStudentsTable
              students={students}
              onRemoveStudent={capabilities.canManageStudents ? setStudentToRemove : undefined}
              onResendInvitation={capabilities.canManageStudents ? handleResendInvitation : undefined}
              resendingInvitationId={resendingInvitationId}
            />
          </CardContent>
        </Card>
      )}

      <Dialog open={studentToRemove !== null} onOpenChange={(open) => !open && setStudentToRemove(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{studentToRemove?.status === 'pending' ? 'Remove Pending Enrollment' : 'Remove Student'}</DialogTitle>
            <DialogDescription>
              {studentToRemove?.status === 'pending' ? (
                <>Are you sure you want to remove the pending enrollment for <strong>{studentToRemove.email}</strong>? This action cannot be undone.</>
              ) : (
                <>Are you sure you want to remove <strong>{studentToRemove?.firstName} {studentToRemove?.lastName}</strong> from the unit? This action cannot be undone.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setStudentToRemove(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => void handleRemoveStudent()}>
              {studentToRemove?.status === 'pending' ? 'Remove Enrollment' : 'Remove Student'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
